import { useState, useEffect } from 'react';
import { DayPicker } from 'react-day-picker';
import { format, isAfter, isBefore, isSameDay, addMonths } from 'date-fns';
import { he, enUS, fr, es } from 'date-fns/locale';
import { useLanguage } from '../contexts/LanguageContext';
import { fetchApartmentAvailability } from '../services/availability-service';
import '../styles/AvailabilityCalendar.css';

// Helper function to get the appropriate locale
const getLocale = (language) => {
  switch (language) {
    case 'he':
      return he;
    case 'fr':
      return fr;
    case 'es':
      return es;
    default:
      return enUS;
  }
};

const AvailabilityCalendar = ({ apartmentId }) => {
  const { t, language } = useLanguage();
  const [bookedDates, setBookedDates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [month, setMonth] = useState(new Date());

  // Function to fetch availability data from external system
  const fetchAvailabilityData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Get current date and date 3 months from now for the range
      const startDate = new Date();
      const endDate = new Date(startDate);
      endDate.setMonth(startDate.getMonth() + 3);
      
      // Call the availability service
      const availabilityData = await fetchApartmentAvailability(apartmentId, startDate, endDate);
      
      // Update state with the fetched data
      setBookedDates(availabilityData);
    } catch (err) {
      console.error('Error fetching availability data:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAvailabilityData();
  }, [apartmentId]);

  // Function to determine if a date is booked
  const isDateBooked = (date) => {
    return bookedDates.some(range => 
      (isAfter(date, range.start) || isSameDay(date, range.start)) && 
      (isBefore(date, range.end) || isSameDay(date, range.end))
    );
  };

  // Function to determine the CSS class for each day
  const getDayClassName = (date) => {
    if (isDateBooked(date)) {
      return 'booked-day';
    }
    return 'available-day';
  };

  // Function to handle month navigation
  const handleMonthChange = (newMonth) => {
    setMonth(newMonth);
  };

  // Function to navigate to next month
  const goToNextMonth = () => {
    setMonth(addMonths(month, 1));
  };

  // Function to navigate to previous month
  const goToPrevMonth = () => {
    setMonth(addMonths(month, -1));
  };

  // Function to refresh availability data
  const handleRefresh = () => {
    fetchAvailabilityData();
  };

  // Get the appropriate locale based on the current language
  const locale = getLocale(language);

  // Custom footer component
  const Footer = () => (
    <div className="calendar-footer">
      <div className="calendar-legend">
        <div className="legend-item">
          <div className="legend-color available"></div>
          <span>{t("available")}</span>
        </div>
        <div className="legend-item">
          <div className="legend-color booked"></div>
          <span>{t("booked")}</span>
        </div>
      </div>
      <button className="refresh-button" onClick={handleRefresh}>
        {t("refreshAvailability")}
      </button>
    </div>
  );

  return (
    <div className="availability-calendar-container">
      <h2 className="calendar-title">{t("availabilityCalendar")}</h2>
      
      {isLoading ? (
        <div className="calendar-loading">
          <div className="loading-spinner"></div>
          <p>{t("loadingAvailability")}</p>
        </div>
      ) : error ? (
        <div className="calendar-error">
          <p>{t("errorLoadingAvailability")}</p>
          <button onClick={handleRefresh} className="retry-button">
            {t("retry")}
          </button>
        </div>
      ) : (
        <div className="calendar-wrapper">
          <div className="calendar-navigation">
            <button onClick={goToPrevMonth} className="month-nav-button">
              {language === 'he' ? '❯' : '❮'}
            </button>
            <h3 className="current-month">
              {format(month, 'MMMM yyyy', { locale })}
            </h3>
            <button onClick={goToNextMonth} className="month-nav-button">
              {language === 'he' ? '❮' : '❯'}
            </button>
          </div>
          
          <DayPicker
            mode="default"
            month={month}
            onMonthChange={handleMonthChange}
            modifiers={{
              booked: (date) => isDateBooked(date),
              available: (date) => !isDateBooked(date)
            }}
            modifiersClassNames={{
              booked: 'booked-day',
              available: 'available-day'
            }}
            locale={locale}
            dir={language === 'he' ? 'rtl' : 'ltr'}
            showOutsideDays
            disabled={[{ before: new Date() }]} // Disable past dates
            footer={<Footer />}
          />
        </div>
      )}
    </div>
  );
};

export default AvailabilityCalendar;
